import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Widths for direct children in a Flex row.
 * The fraction values match Split's `ratio` vocabulary and apply to two blocks.
 */
export type FlexChildrenRatio =
  | 'equal'
  | '1/3-2/3'
  | '2/3-1/3'
  | '1/4-3/4'
  | '3/4-1/4';

export const FLEX_CHILDREN_RATIO_TOKENS: readonly FlexChildrenRatio[] = [
  'equal',
  '1/3-2/3',
  '2/3-1/3',
  '1/4-3/4',
  '3/4-1/4',
];

/**
 * How a Flex clips or scrolls content that does not fit.
 * `visible` is the default and adds no classes.
 */
export type FlexOverflow =
  | 'visible'
  | 'overflow-x: auto'
  | 'overflow-x: hidden'
  | 'overflow-y: auto'
  | 'overflow-y: hidden'
  | 'overflow: auto'
  | 'overflow: hidden';

export const FLEX_OVERFLOW_TOKENS: readonly FlexOverflow[] = [
  'visible',
  'overflow-x: auto',
  'overflow-x: hidden',
  'overflow-y: auto',
  'overflow-y: hidden',
  'overflow: auto',
  'overflow: hidden',
];

/**
 * Flex variant styles using Class Variance Authority (CVA)
 *
 * Defines direction, gap, wrap, alignment, and justification options for flex layouts.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Direction (base and responsive at md, lg breakpoints)
 * - column: Vertical stacking (replaces Stack)
 * - row: Horizontal layout (replaces Cluster)
 *
 * ## Gap Options (spacing between children)
 * - xxs through 3xl matching design tokens
 * - l1: 10px semantic spacing (e.g. ExpertQuote vertical stack)
 *
 * ## Wrap Options
 * - wrap: Items wrap to next line
 * - nowrap: Items stay on single line
 *
 * ## Alignment Options (cross-axis)
 * - start, center, end, stretch
 *
 * ## Justify Options (main-axis)
 * - start, center, end, between
 *
 * ## Children ratio (row only)
 * Fraction values set unequal widths for two direct children. Same child-selector
 * basis classes as Split's `ratio`, plus `min-w-0` so long content shrinks
 * instead of pushing the row wider — see {@link getFlexChildrenRatioClasses}.
 *
 * ## Overflow
 * Opt-in scroll or clip. Omitted and `visible` add no classes — see
 * {@link getFlexOverflowClasses}.
 */
export const flexVariants = cva('flex', {
  variants: {
    direction: {
      column: 'flex-col',
      row: 'flex-row',
    },
    'direction-md': {
      column: 'md:flex-col',
      row: 'md:flex-row',
    },
    'direction-lg': {
      column: 'lg:flex-col',
      row: 'lg:flex-row',
    },
    gap: {
      none: 'gap-0',
      min: 'gap-min',
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
      l1: 'gap-l1',
      max: 'gap-max',
    },
    wrap: {
      wrap: 'flex-wrap',
      nowrap: 'flex-nowrap',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    },
    'align-md': {
      start: 'md:items-start',
      center: 'md:items-center',
      end: 'md:items-end',
      stretch: 'md:items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
    /** When true, all direct children get flex-1 min-w-0 so they share space equally and fill the container. */
    childrenFill: {
      true: 'flex-nowrap [&>*]:min-w-0 [&>*]:flex-1',
      false: '',
    },
    /**
     * Width of the flex container.
     * - full: w-full
     * - auto: w-auto (default)
     * - article: centered, max article measure (mx-auto max-w-article-content w-full)
     * - max-content: as wide as its content, and a parent row cannot shrink it
     */
    width: {
      full: 'w-full',
      auto: 'w-auto',
      article: 'mx-auto max-w-article-content w-full',
      'max-content': 'w-max shrink-0',
    },
    height: {
      auto: 'h-auto',
      full: 'h-full',
    },
  },
  defaultVariants: {
    direction: 'column',
    gap: 'md',
    wrap: 'nowrap',
    align: 'stretch',
    justify: 'start',
    childrenFill: false,
    width: 'auto',
    height: 'auto',
  },
});

export type FlexVariantProps = VariantProps<typeof flexVariants>;

/** `1` allows shrinking. `0` keeps the layout's width inside a parent row. */
export const FLEX_SHRINK = ['1', '0'] as const;

export type FlexShrink = (typeof FLEX_SHRINK)[number];

/**
 * Class for an explicit shrink choice. Omitted and empty add nothing, so the
 * layout keeps the usual flex shrink. `1` is emitted so it can replace the
 * `shrink-0` that Width max-content adds.
 */
export const getFlexShrinkClass = (
  shrink: string | null | undefined,
): string | undefined => {
  if (shrink === '0') return 'shrink-0';
  if (shrink === '1') return 'shrink';
  return undefined;
};

type FlexDirection = 'row' | 'column';

/**
 * Ratio classes for a row, written out in full for each breakpoint prefix so
 * Tailwind can find them. Fraction values also set `min-w-0` so long content
 * shrinks instead of pushing the row wider, and `flex-nowrap` because the two
 * widths already fill the row: with wrapping on, the gap would push the second
 * block onto its own line.
 */
const RATIO_CLASSES = {
  base: {
    equal: 'flex-nowrap [&>*]:min-w-0 [&>*]:basis-1/2 [&>*]:grow [&>*]:shrink',
    '1/3-2/3':
      'flex-nowrap [&>*]:min-w-0 [&>*:first-child]:basis-1/3 [&>*:not(:first-child)]:basis-2/3',
    '2/3-1/3':
      'flex-nowrap [&>*]:min-w-0 [&>*:first-child]:basis-2/3 [&>*:not(:first-child)]:basis-1/3',
    '1/4-3/4':
      'flex-nowrap [&>*]:min-w-0 [&>*:first-child]:basis-1/4 [&>*:not(:first-child)]:basis-3/4',
    '3/4-1/4':
      'flex-nowrap [&>*]:min-w-0 [&>*:first-child]:basis-3/4 [&>*:not(:first-child)]:basis-1/4',
  },
  md: {
    equal:
      'md:flex-nowrap md:[&>*]:min-w-0 md:[&>*]:basis-1/2 md:[&>*]:grow md:[&>*]:shrink',
    '1/3-2/3':
      'md:flex-nowrap md:[&>*]:min-w-0 md:[&>*:first-child]:basis-1/3 md:[&>*:not(:first-child)]:basis-2/3',
    '2/3-1/3':
      'md:flex-nowrap md:[&>*]:min-w-0 md:[&>*:first-child]:basis-2/3 md:[&>*:not(:first-child)]:basis-1/3',
    '1/4-3/4':
      'md:flex-nowrap md:[&>*]:min-w-0 md:[&>*:first-child]:basis-1/4 md:[&>*:not(:first-child)]:basis-3/4',
    '3/4-1/4':
      'md:flex-nowrap md:[&>*]:min-w-0 md:[&>*:first-child]:basis-3/4 md:[&>*:not(:first-child)]:basis-1/4',
  },
  lg: {
    equal:
      'lg:flex-nowrap lg:[&>*]:min-w-0 lg:[&>*]:basis-1/2 lg:[&>*]:grow lg:[&>*]:shrink',
    '1/3-2/3':
      'lg:flex-nowrap lg:[&>*]:min-w-0 lg:[&>*:first-child]:basis-1/3 lg:[&>*:not(:first-child)]:basis-2/3',
    '2/3-1/3':
      'lg:flex-nowrap lg:[&>*]:min-w-0 lg:[&>*:first-child]:basis-2/3 lg:[&>*:not(:first-child)]:basis-1/3',
    '1/4-3/4':
      'lg:flex-nowrap lg:[&>*]:min-w-0 lg:[&>*:first-child]:basis-1/4 lg:[&>*:not(:first-child)]:basis-3/4',
    '3/4-1/4':
      'lg:flex-nowrap lg:[&>*]:min-w-0 lg:[&>*:first-child]:basis-3/4 lg:[&>*:not(:first-child)]:basis-1/4',
  },
} satisfies Record<'base' | 'md' | 'lg', Record<FlexChildrenRatio, string>>;

/**
 * Undoes the ratio widths when a row turns into a column at a breakpoint.
 * The first-child and not-first-child selectors have to be reset too: they are
 * more specific than `[&>*]`, so a plain reset would lose to them.
 */
const RATIO_RESET_CLASSES: Record<'md' | 'lg', string> = {
  md: 'md:[&>*]:basis-auto md:[&>*]:grow-0 md:[&>*:first-child]:basis-auto md:[&>*:not(:first-child)]:basis-auto',
  lg: 'lg:[&>*]:basis-auto lg:[&>*]:grow-0 lg:[&>*:first-child]:basis-auto lg:[&>*:not(:first-child)]:basis-auto',
};

/** Puts the author's wrapping back when a row (which forces no-wrap) turns into a column. */
function withWrapRestored(
  resetClasses: string,
  wrapClass: string,
  wrap: 'wrap' | 'nowrap' | null | undefined,
): string {
  return wrap === 'wrap' ? `${resetClasses} ${wrapClass}` : resetClasses;
}

function ratioResetClasses(
  breakpoint: 'md' | 'lg',
  wrap: 'wrap' | 'nowrap' | null | undefined,
): string {
  const wrapClass = breakpoint === 'md' ? 'md:flex-wrap' : 'lg:flex-wrap';
  return withWrapRestored(RATIO_RESET_CLASSES[breakpoint], wrapClass, wrap);
}

/**
 * Classes that size direct children wherever the Flex is a row.
 *
 * Follows the direction at each screen size: ratio classes are added where the
 * layout becomes a row, and reset where it goes back to a column. A layout
 * that is a column at every size gets no classes. A ratio this component no
 * longer offers, such as a stored `peek` or `max-content`, adds none.
 */
export function getFlexChildrenRatioClasses(
  ratio: FlexChildrenRatio | null | undefined,
  direction: FlexDirection | null | undefined,
  directionMd: FlexDirection | null | undefined,
  directionLg: FlexDirection | null | undefined,
  wrap: 'wrap' | 'nowrap' | null | undefined,
): string {
  if (!ratio || !FLEX_CHILDREN_RATIO_TOKENS.includes(ratio)) return '';

  const baseDirection = direction ?? 'column';
  const mdDirection = directionMd ?? baseDirection;
  const lgDirection = directionLg ?? mdDirection;
  const classes: string[] = [];

  if (baseDirection === 'row') classes.push(RATIO_CLASSES.base[ratio]);

  if (mdDirection !== baseDirection) {
    classes.push(
      mdDirection === 'row'
        ? RATIO_CLASSES.md[ratio]
        : ratioResetClasses('md', wrap),
    );
  }

  if (lgDirection !== mdDirection) {
    classes.push(
      lgDirection === 'row'
        ? RATIO_CLASSES.lg[ratio]
        : ratioResetClasses('lg', wrap),
    );
  }

  return classes.join(' ');
}

/**
 * Classes for the overflow control. `visible` and an omitted value add none,
 * so existing Flex layouts stay unchanged.
 * `min-w-0` / `min-h-0` let a Flex nested in another flex container scroll
 * instead of growing with its content.
 */
const OVERFLOW_CLASSES: Record<FlexOverflow, string> = {
  visible: '',
  'overflow-x: auto': 'min-w-0 max-w-full overflow-x-auto',
  'overflow-x: hidden': 'min-w-0 max-w-full overflow-x-hidden',
  'overflow-y: auto': 'min-h-0 max-h-full overflow-y-auto',
  'overflow-y: hidden': 'min-h-0 max-h-full overflow-y-hidden',
  'overflow: auto': 'min-h-0 min-w-0 overflow-auto',
  'overflow: hidden': 'min-h-0 min-w-0 overflow-hidden',
};

export function getFlexOverflowClasses(
  overflow: FlexOverflow | null | undefined,
): string {
  if (!overflow || overflow === 'visible') return '';
  return OVERFLOW_CLASSES[overflow];
}

type FlexJustify = 'start' | 'center' | 'end' | 'between';

function overflowsHorizontally(
  overflow: FlexOverflow | null | undefined,
): boolean {
  return (
    overflow === 'overflow-x: auto' ||
    overflow === 'overflow-x: hidden' ||
    overflow === 'overflow: auto' ||
    overflow === 'overflow: hidden'
  );
}

/**
 * `justify-center` and `justify-end` clip the start of a horizontal row once
 * the children are wider than the container. `safe` uses the requested
 * alignment when the items fit and falls back to the start when they do not.
 * Tailwind 3.3 has no `justify-center-safe` utility, so this is an arbitrary
 * property. A column keeps normal alignment: `justify` is the vertical axis
 * there, and a horizontal overflow does not change it.
 */
const SAFE_JUSTIFY_CLASS = {
  center: '[justify-content:safe_center]',
  end: '[justify-content:safe_flex-end]',
} as const satisfies Partial<Record<FlexJustify, string>>;

export function getFlexJustifyForOverflow(
  justify: FlexJustify | null | undefined,
  overflow: FlexOverflow | null | undefined,
  direction: FlexDirection | null | undefined,
): { justify: FlexJustify | null | undefined; className: string } {
  const safeClass =
    justify === 'center' || justify === 'end'
      ? SAFE_JUSTIFY_CLASS[justify]
      : '';
  if (
    safeClass &&
    (direction ?? 'column') === 'row' &&
    overflowsHorizontally(overflow)
  ) {
    return { justify: 'start', className: safeClass };
  }
  return { justify, className: '' };
}
